import React from 'react';
import { Tag } from 'antd';
import { get, isArray, isPlainObject } from 'lodash';

export const asString = (value: any) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  try {
    return String(value);
  } catch {
    return '';
  }
};

export const toSafeNumber = (value: any, fallback = 0) => {
  if (value === null || value === undefined || value === '') return fallback;
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : fallback;
};

export const isTruthy01 = (value: any) => value === 1 || value === '1' || value === true || value === '是';

export const isFalsy01 = (value: any) => value === 0 || value === '0' || value === false || value === '否';

export const renderValue = (value: any): React.ReactNode => {
  if (isTruthy01(value)) return React.createElement(Tag, { color: 'red' }, '是');
  if (isFalsy01(value)) return React.createElement(Tag, { color: 'green' }, '否');
  const str = asString(value);
  return str === '' ? '-' : str;
};

/**
 * 以“原样文本”方式渲染值：不对 0/1 做布尔标签转换
 */
export const renderPlainValue = (value: any): React.ReactNode => {
  const str = asString(value);
  return str === '' ? '-' : str;
};

export const renderBoolTag = (
  value: any,
  options?: { truthyColor?: string; falsyColor?: string; truthyText?: string; falsyText?: string; emptyText?: React.ReactNode },
): React.ReactNode => {
  const { truthyColor = 'green', falsyColor = 'red', truthyText = '是', falsyText = '否', emptyText = '-' } = options || {};
  if (isTruthy01(value)) return React.createElement(Tag, { color: truthyColor }, truthyText);
  if (isFalsy01(value)) return React.createElement(Tag, { color: falsyColor }, falsyText);
  return emptyText;
};

export const toHitMissTag = (value: any): React.ReactNode => {
  const str = asString(value).trim();
  
  // 判定为“未命中”的情况
  if (isFalsy01(value) || value === '' || str === '-' || str === '一致' || str === '正常') {
    return React.createElement(Tag, { color: 'blue' }, '未命中');
  }

  // 其他情况统一判定为“命中”
  return React.createElement(Tag, { color: 'red' }, '命中');
};

export const toPassFailTag = (value: any): React.ReactNode => {
  if (isTruthy01(value)) return React.createElement(Tag, { color: 'green' }, '通过');
  if (isFalsy01(value)) return React.createElement(Tag, { color: 'red' }, '未通过');
  return renderValue(value);
};

export const normalizeRiskQualitative = (value: string) => {
  const v = value.trim();
  if (!v) return '';
  if (v === '通过') return '风控通过';
  if (v === '拒绝') return '风控拒绝';
  if (v.startsWith('风控')) return v;
  if (v.includes('通过') || v.includes('拒绝')) return `风控${v}`;
  return v;
};

export const toObjectFromRrzuKeyValue = (items?: Array<{ Key?: string; Value?: any }>): Record<string, any> => {
  if (!isArray(items)) return {};
  return items.reduce<Record<string, any>>((acc, kv) => {
    if (kv && typeof kv.Key === 'string') acc[kv.Key] = kv.Value;
    return acc;
  }, {});
};

export const toRowsFromRrzuKeyValue = (rows?: Array<Array<{ Key?: string; Value?: any }>>) => {
  if (!isArray(rows)) return [];
  return rows.map((r) => toObjectFromRrzuKeyValue(r));
};

export const toSectionMap = (data: any): Record<string, any> => {
  if (isArray(data)) {
    return data.reduce<Record<string, any>>((acc, sec) => {
      const key = sec?.Key;
      if (typeof key === 'string') acc[key] = sec?.Value;
      return acc;
    }, {});
  }
  if (isPlainObject(data)) return data as Record<string, any>;
  return {};
};

export const isTableValue = (val: any): boolean => {
  return isArray(val) && val.some((v: any) => v?.Key === 'th' || v?.Key === 'td');
};

export const parseInnerTable = (value: any) => {
  if (isPlainObject(value)) {
    const headers = get(value, 'th');
    const td = get(value, 'td');
    const title = get(value, 'title');
    if (isArray(headers) && isArray(td)) {
      const rows = td.map((row: any, idx: number) => {
        if (isPlainObject(row)) return { ...row, key: idx };
        if (isArray(row)) return { ...toObjectFromRrzuKeyValue(row as any), key: idx };
        return { key: idx };
      });
      const keys = rows.length ? Object.keys(rows[0]).filter((k) => k !== 'key') : [];
      return { title, headers: headers as any[], keys, rows };
    }
  }

  if (isArray(value)) {
    const titleObj = value.find((v) => v?.Key === 'title');
    const thObj = value.find((v) => v?.Key === 'th');
    const tdObj = value.find((v) => v?.Key === 'td');
    const headers = thObj?.Value;
    const td = tdObj?.Value;
    if (isArray(headers) && isArray(td)) {
      const firstRow = isArray(td[0]) ? td[0] : [];
      const keys = isArray(firstRow) ? firstRow.map((kv: any) => kv?.Key).filter((k: any) => typeof k === 'string') : [];
      return {
        title: titleObj?.Value,
        headers,
        keys,
        rows: td.map((row: any, idx: number) => ({ ...toObjectFromRrzuKeyValue(row), key: idx })),
      };
    }
  }

  return null;
};

export const formatDateTime = (value: any) => {
  if (!value) return '-';
  const raw = String(value);
  const asNumber = Number(raw);
  let date: Date | null = null;
  if (!Number.isNaN(asNumber) && raw.length >= 10) {
    const ms = raw.length >= 13 ? asNumber : asNumber * 1000;
    date = new Date(ms);
  } else {
    const parsed = Date.parse(raw);
    if (!Number.isNaN(parsed)) date = new Date(parsed);
  }
  if (!date || Number.isNaN(date.getTime())) return raw;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

export const addDays = (value: any, days: number) => {
  if (!value) return '-';
  const raw = String(value);
  const asNumber = Number(raw);
  let date: Date | null = null;
  if (!Number.isNaN(asNumber) && raw.length >= 10) {
    const ms = raw.length >= 13 ? asNumber : asNumber * 1000;
    date = new Date(ms);
  } else {
    const parsed = Date.parse(raw);
    if (!Number.isNaN(parsed)) date = new Date(parsed);
  }
  if (!date || Number.isNaN(date.getTime())) return '-';
  const next = new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())} ${pad(next.getHours())}:${pad(next.getMinutes())}:${pad(next.getSeconds())}`;
};

export const formatPercent = (value: any) => {
  if (value === undefined || value === null || value === '') return '-';
  if (typeof value === 'string' && value.includes('%')) return value;
  const num = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(num)) return String(value);
  const pct = num <= 1 ? num * 100 : num;
  return `${pct.toFixed(1)}%`;
};

export const maskPhone = (phone?: string) => {
  if (!phone) return '-';
  return phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2');
};

export const maskIdCard = (idCard?: string) => {
  if (!idCard) return '-';
  return idCard.replace(/(\d{6})\d{8}(\d{4})/, '$1********$2');
};

export const getAgeFromIdCard = (idCard: string) => {
  if (!idCard || idCard.length < 10) return '-';
  const year = idCard.substring(6, 10);
  const currentYear = new Date().getFullYear();
  return currentYear - parseInt(year, 10);
};

export const getGenderFromIdCard = (idCard: string) => {
  if (!idCard || idCard.length < 17) return '-';
  const val = parseInt(idCard.substring(16, 17), 10);
  return val % 2 === 1 ? '男' : '女';
};
